import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SendTemplateDialog } from "@/components/SendTemplateDialog";
import { EditLeadDialog } from "@/components/EditLeadDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ArrowLeft, MessageSquare, Phone, Send, Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { PriorityBadge } from "@/lib/priority";

const TONES = {
  New: "bg-muted text-foreground/70",
  Messaged: "bg-fun-blue-tint text-fun-blue",
  Responded: "bg-fun-yellow-tint text-fun-yellow",
  Registered: "bg-fun-orange-tint text-fun-orange",
  Confirmed: "bg-fun-aqua-tint text-fun-aqua",
  Attended: "bg-primary text-primary-foreground",
  "No-Show": "bg-destructive/10 text-destructive",
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [convo, setConvo] = useState({ messages: [], session_open: false });

  async function removeLead() {
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function load() {
    try {
      const [{ data: l }, { data: c }] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get(`/inbox/${leadId}`),
      ]);
      setLead(l);
      setConvo(c);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  if (!lead) return <div className="p-10 text-muted-foreground">Loading…</div>;

  // Most recent programme — fills the event/date/venue template variables.
  const latestEvent = lead.event_history?.[lead.event_history.length - 1]?.event;

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-10" data-testid="lead-detail-page">
      <Link
        to="/leads"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </Link>
      <PageHeader
        overline="Lead"
        title={lead.name}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <Phone className="h-3.5 w-3.5" /> <span className="font-mono">{lead.phone}</span>
            <PriorityBadge priority={lead.priority} />
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <SendTemplateDialog
              lead={lead}
              event={latestEvent}
              onSent={load}
              trigger={
                <Button variant="outline" data-testid="send-template-btn">
                  <Send className="mr-2 h-4 w-4" /> Send template
                </Button>
              }
            />
            <Button asChild data-testid="open-chat-btn">
              <Link to={`/inbox/${lead.id}`}>
                <MessageSquare className="mr-2 h-4 w-4" /> Open chat
              </Link>
            </Button>
            <EditLeadDialog
              lead={lead}
              onSaved={load}
              trigger={
                <Button variant="ghost" size="icon" title="Edit lead" data-testid="lead-edit-btn">
                  <Pencil className="h-4 w-4" />
                </Button>
              }
            />
            <ConfirmDialog
              testId="lead-delete-dialog"
              title={`Delete ${lead.name}?`}
              description={
                `This removes the lead, all ${convo.messages.length} message(s) in their ` +
                "conversation and their programme links. This cannot be undone."
              }
              onConfirm={removeLead}
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  title="Delete lead"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  data-testid="lead-delete-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              }
            />
          </div>
        }
      />

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card className="border-border p-6 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            Programmes
          </div>
          <h3 className="mt-1 font-heading text-lg font-semibold">Attendance history</h3>
          <div className="mt-4 divide-y divide-border">
            {lead.event_history.length === 0 && (
              <div className="py-6 text-sm text-muted-foreground">
                Not associated with any programme yet.
              </div>
            )}
            {lead.event_history.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold">{h.event?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">
                    {h.event?.date ? safeDate(h.event.date) : ""}
                  </div>
                </div>
                <Badge className={`${TONES[h.status]} rounded-full`}>{h.status}</Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card className="border-border p-6 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            Conversation
          </div>
          <h3 className="mt-1 font-heading text-lg font-semibold">Recent messages</h3>
          <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto">
            {convo.messages.length === 0 && (
              <div className="py-6 text-sm text-muted-foreground">No messages yet.</div>
            )}
            {convo.messages.slice(-30).map((m) => (
              <div
                key={m.id}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  m.direction === "out"
                    ? "ml-auto bg-accent text-accent-foreground"
                    : "border border-border bg-card"
                }`}
              >
                <div>{m.text}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {m.kind} · {safeTime(m.created_at)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function safeDate(d) {
  try {
    return format(new Date(d), "PPP p");
  } catch {
    return d;
  }
}
function safeTime(d) {
  try {
    return format(new Date(d), "PP p");
  } catch {
    return d;
  }
}
